import { useEffect, useState } from "react";
import { Archive, Pencil, Plus, X } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const slugify = (t) =>
  t.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").slice(0, 80);

const EMPTY = {
  title: "",
  slug: "",
  category: "Insights",
  excerpt: "",
  content: "",
  cover_image: "",
  publish_at: "",
  status: "draft",
  seo_title: "",
  seo_description: "",
};

const PostEditor = ({ post, onClose, onSaved }) => {
  const isNew = !post.id;
  const [f, setF] = useState(
    isNew
      ? EMPTY
      : {
          title: post.title,
          slug: post.slug,
          category: post.category,
          excerpt: post.excerpt,
          content: post.content,
          cover_image: post.cover_image || "",
          publish_at: post.publish_at ? post.publish_at.slice(0, 16) : "",
          status: post.status === "archived" ? "draft" : post.status,
          seo_title: post.seo?.title || "",
          seo_description: post.seo?.description || "",
        }
  );
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => {
    const v = e.target.value;
    setF((prev) => {
      const next = { ...prev, [k]: v };
      if (k === "title" && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setState("saving");
    setError("");
    const payload = {
      title: f.title,
      slug: f.slug,
      category: f.category,
      excerpt: f.excerpt,
      content: f.content,
      cover_image: f.cover_image || null,
      status: f.status,
      publish_at: f.publish_at ? new Date(f.publish_at).toISOString() : null,
      seo: { title: f.seo_title || f.title, description: f.seo_description || f.excerpt },
    };
    try {
      const r = isNew ? await api.post("/admin/posts", payload) : await api.put(`/admin/posts/${post.id}`, payload);
      onSaved(r.data, isNew);
    } catch (err) {
      setState("error");
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 p-0 backdrop-blur-sm sm:p-6" onClick={onClose} data-testid="post-editor-modal">
      <form onSubmit={save} className="h-full w-full max-w-3xl space-y-6 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:max-h-[92vh] sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">{isNew ? "New article" : "Edit article"}</p>
            <h2 className="mt-1 font-display text-xl font-bold text-brand-snow">{isNew ? "Write something worth reading" : post.title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="post-editor-close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Title" htmlFor="pe-title" required><TextInput id="pe-title" testid="pe-title" value={f.title} onChange={set("title")} required /></Field>
          <Field label="URL slug" htmlFor="pe-slug" hint="Auto-generated from the title; editable.">
            <TextInput id="pe-slug" testid="pe-slug" value={f.slug} onChange={(e) => { setSlugTouched(true); setF({ ...f, slug: slugify(e.target.value) }); }} required />
          </Field>
          <Field label="Category" htmlFor="pe-category"><TextInput id="pe-category" testid="pe-category" value={f.category} onChange={set("category")} /></Field>
          <Field label="Cover image URL" htmlFor="pe-cover"><TextInput id="pe-cover" testid="pe-cover" value={f.cover_image} onChange={set("cover_image")} placeholder="Optional — https://…" /></Field>
          <Field label="Status" htmlFor="pe-status">
            <SelectInput id="pe-status" testid="pe-status" value={f.status} onChange={set("status")}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </SelectInput>
          </Field>
          <Field label="Publish date & time" htmlFor="pe-publish-at" hint="Scheduled posts go live automatically at this time (your local time).">
            <input id="pe-publish-at" type="datetime-local" data-testid="pe-publish-at" value={f.publish_at} onChange={set("publish_at")}
              className="w-full rounded-md border border-brand-mist/20 bg-brand-charcoal px-4 py-3 text-sm text-brand-snow [color-scheme:dark] focus:border-brand-emerald focus:outline-none" />
          </Field>
        </div>

        <Field label="Excerpt" htmlFor="pe-excerpt" required hint="Shown on cards and used as the default meta description.">
          <TextArea id="pe-excerpt" rows={2} testid="pe-excerpt" value={f.excerpt} onChange={set("excerpt")} required />
        </Field>
        <Field label="Article body" htmlFor="pe-content" required hint="Blank line between paragraphs. Start a line with '## ' for a heading, '- ' for bullets.">
          <TextArea id="pe-content" rows={12} testid="pe-content" value={f.content} onChange={set("content")} required />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="SEO title" htmlFor="pe-seo-title"><TextInput id="pe-seo-title" testid="pe-seo-title" value={f.seo_title} onChange={set("seo_title")} /></Field>
          <Field label="SEO description" htmlFor="pe-seo-desc"><TextInput id="pe-seo-desc" testid="pe-seo-desc" value={f.seo_description} onChange={set("seo_description")} /></Field>
        </div>

        {state === "error" && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="post-editor-error">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={state === "saving"} data-testid="post-editor-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black transition-colors hover:bg-brand-snow disabled:opacity-60">
            {state === "saving" ? "Saving…" : isNew ? "Create article" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} className="rounded-full border border-brand-mist/25 px-6 py-3 text-sm font-medium text-brand-mist hover:text-brand-snow">Cancel</button>
        </div>
      </form>
    </div>
  );
};

const STATUS_STYLE = {
  published: "text-brand-emerald",
  scheduled: "text-brand-azure",
  draft: "text-yellow-300",
  archived: "text-brand-mist",
};

const AdminPosts = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => setError("Could not load articles."));
  useEffect(() => { load(); }, []);

  const onSaved = (saved, isNew) => {
    setEditing(null);
    load();
  };

  const archive = async (p) => {
    await api.delete(`/admin/posts/${p.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-posts">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Resources & articles</h1>
          <p className="text-sm text-brand-mist">{posts.length} article{posts.length === 1 ? "" : "s"}. Draft, schedule and publish with per-article SEO.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="post-new" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-5 py-2.5 font-display text-sm font-semibold text-brand-black transition-colors hover:bg-brand-snow">
          <Plus size={15} /> New article
        </button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="surface-card overflow-x-auto" data-testid="posts-table">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead>
            <tr className="border-b border-brand-mist/10 text-xs uppercase tracking-wider text-brand-mist">
              <th className="px-5 py-3.5 font-medium">Article</th>
              <th className="px-5 py-3.5 font-medium">Category</th>
              <th className="px-5 py-3.5 font-medium">Status</th>
              <th className="px-5 py-3.5 font-medium">Publish date</th>
              <th className="px-5 py-3.5 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-mist/8">
            {posts.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-brand-mist">No articles yet — write the first one.</td></tr>}
            {posts.map((p) => (
              <tr key={p.id} data-testid={`post-row-${p.slug}`} className="transition-colors hover:bg-white/5">
                <td className="px-5 py-3.5">
                  <p className="font-medium text-brand-snow">{p.title}</p>
                  <p className="text-xs text-brand-mist">/resources/{p.slug}</p>
                </td>
                <td className="px-5 py-3.5 text-brand-mist">{p.category}</td>
                <td className="px-5 py-3.5">
                  <span className={`text-xs font-medium capitalize ${STATUS_STYLE[p.display_status || p.status] || "text-brand-mist"}`} data-testid={`post-status-${p.slug}`}>
                    {p.display_status || p.status}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-brand-mist">
                  {p.publish_at ? new Date(p.publish_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-2">
                    <button onClick={() => api.get(`/admin/posts/${p.id}`).then((r) => setEditing(r.data))} data-testid={`post-edit-${p.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs font-medium text-brand-snow hover:border-brand-emerald hover:text-brand-emerald">
                      <Pencil size={12} /> Edit
                    </button>
                    {p.status !== "archived" && (
                      <button onClick={() => archive(p)} data-testid={`post-archive-${p.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs font-medium text-brand-mist hover:border-red-400 hover:text-red-400">
                        <Archive size={12} /> Archive
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PostEditor post={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
};

export default AdminPosts;
